import { useRef, useState, useEffect } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

export function TextInput2({ label, icon, placeholder, value, name, onChange, disabled }:
    { label?: any, icon?: any, placeholder?: any, value: any, name?: any, onChange: any, disabled?: any }) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleContainerClick = () => {
        if (!disabled && inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <div>
            {label &&
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            }
            <div
                onClick={handleContainerClick}
                className={`flex items-center rounded-md ring-1 ring-gray-200 bg-white py-2 px-3
                transition-colors duration-200
                ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'hover:ring-gray-300 cursor-text'}`}>
                {icon && <span className="mr-2 text-gray-400 shrink-0">{icon}</span>}
                <input
                    ref={inputRef}
                    type={'text'}
                    name={name}
                    className={`appearance-none border-none bg-transparent w-full text-gray-900 px-1
                    leading-tight focus:outline-none md:text-sm placeholder:text-sm placeholder:text-gray-400
                    ${disabled ? 'cursor-not-allowed' : ''}
                    `}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            </div>
        </div>
    )
}

export function Selectinput({ label, onChange, className, name, value, options }: { options: any; value: string; name: string, label: string, onChange: any, className?: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggle = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = 240;

            setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
        }
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearchTerm("");
        }
    };

    const selectedOption = options.find((opt: any) => opt.value === value) || options[0];
    const displayValue = selectedOption?.label || 'Select an option';

    const handleSelect = (optionValue: string) => {
        const syntheticEvent = {
            target: {
                name,
                value: optionValue
            }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
        setIsOpen(false);
        setSearchTerm("");
    };

    const filteredOptions = options.filter((option: any) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={`${className ?? ''}`} ref={dropdownRef}>
            {label &&
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            }
            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={handleToggle}
                    className="flex w-full items-center justify-between gap-2 rounded-lg bg-white py-2 px-3 text-left ring-1 ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                    <span className={`truncate md:text-sm leading-tight ${value ? 'text-gray-700' : 'text-gray-400'}`}>
                        {displayValue}
                    </span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className={`absolute z-20 w-full flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ${
                        openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}>
                        <div className="p-2 border-b border-gray-200">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Search..."
                                    className="w-full rounded-md py-2 pl-9 pr-3 text-base md:text-sm ring-1 ring-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="max-h-60 overflow-auto">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option: any) => {
                                    const isSelected = value === option.value;
                                    const isPlaceholder = option.value === '';
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => handleSelect(option.value)}
                                            className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium transition-colors
                                                ${isPlaceholder
                                                    ? 'text-gray-400 hover:bg-gray-50'
                                                    : isSelected
                                                        ? 'bg-blue-50 text-blue-700 font-semibold'
                                                        : 'text-gray-900 hover:bg-gray-50'}`}
                                        >
                                            <span className="truncate">{option.label}</span>
                                            {isSelected && !isPlaceholder && <Check className="h-4 w-4 shrink-0 text-blue-600" />}
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="px-3 py-6 text-center text-sm text-gray-500">No results found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
